/** Shared helpers for task/sub-task assignment edit forms. */

export type AssignmentSource = {
  assignedTo?: Array<{ id: number; firstName?: string; lastName?: string; email?: string }>;
  assignedToPositionIds?: number[];
  assignedToPositions?: Array<{ id: number; title?: string; name?: string }>;
  assignedToScheduleIds?: number[];
  assignedToSchedules?: Array<{ id: number; name?: string }>;
};

const toId = (v: unknown): number | null => {
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? n : null;
};

export function extractAssignmentFormIds(source: AssignmentSource) {
  const assignedToEmployeeIds = (source.assignedTo ?? [])
    .map(a => toId(a.id))
    .filter((n): n is number => n != null);

  const fromPositionIds = (source.assignedToPositionIds ?? [])
    .map(toId)
    .filter((n): n is number => n != null);
  const fromPositions = (source.assignedToPositions ?? [])
    .map(p => toId(p.id))
    .filter((n): n is number => n != null);
  const assignedToPositionIds = fromPositionIds.length > 0 ? fromPositionIds : fromPositions;

  const fromScheduleIds = (source.assignedToScheduleIds ?? [])
    .map(toId)
    .filter((n): n is number => n != null);
  const fromSchedules = (source.assignedToSchedules ?? [])
    .map(s => toId(s.id))
    .filter((n): n is number => n != null);
  const assignedToScheduleIds = fromScheduleIds.length > 0 ? fromScheduleIds : fromSchedules;

  return { assignedToEmployeeIds, assignedToPositionIds, assignedToScheduleIds };
}

export function employeeBasketExtras(source: AssignmentSource) {
  return (source.assignedTo ?? []).map(e => {
    const name = `${e.firstName ?? ''} ${e.lastName ?? ''}`.trim();
    const label = name ? (e.email ? `${name} (${e.email})` : name) : e.email || `#${e.id}`;
    return { id: e.id, label };
  });
}

export function positionBasketExtras(source: AssignmentSource) {
  const fromObjects = (source.assignedToPositions ?? []).map(p => ({
    id: p.id,
    label: p.title || p.name || `#${p.id}`,
  }));
  if (fromObjects.length > 0) return fromObjects;
  return (source.assignedToPositionIds ?? []).map(id => ({ id, label: `#${id}` }));
}

export function scheduleBasketExtras(source: AssignmentSource) {
  const fromObjects = (source.assignedToSchedules ?? []).map(s => ({
    id: s.id,
    label: s.name || `#${s.id}`,
  }));
  if (fromObjects.length > 0) return fromObjects;
  return (source.assignedToScheduleIds ?? []).map(id => ({ id, label: `#${id}` }));
}
